// src/components/maps/preview/NavigationSimulator.jsx
// Step-by-step waypoint navigation using route step photos
import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, ImageOverlay, CircleMarker, Polyline, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Replay as RestartIcon,
  ArrowUpward as ArrowUpIcon,
  Stairs as StairsIcon,
  Elevator as ElevatorIcon,
  CheckCircle as CheckIcon,
  CameraAlt as CameraIcon,
  Map as MapIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import ArrivalScreen from '../../navigation/ArrivalScreen';
import { API_BASE_URL } from '../../../config/constants';

// Step Photo View - displays the current step's photo and instruction
const StepPhotoView = ({ currentStep, stepNumber, totalSteps, onNext, onPrev, isLastStep }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Get the photo URL from the current step
  const photoUrl = useMemo(() => {
    console.log('[NavigationSimulator] Current step:', currentStep);
    console.log('[NavigationSimulator] photo_url:', currentStep?.photo_url);
    if (!currentStep?.photo_url) return null;
    let url = currentStep.photo_url;
    // Transform /uploads/ to /api/uploads/ for correct API path
    if (url.startsWith('/uploads/')) {
      url = url.replace('/uploads/', '/api/uploads/');
    }
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    console.log('[NavigationSimulator] Full photo URL:', fullUrl);
    return fullUrl;
  }, [currentStep?.photo_url]);

  // Reset image state when step changes
  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
  }, [currentStep?.step_number, photoUrl]);

  // Get direction icon based on instruction
  const getDirectionIcon = () => {
    const instruction = (currentStep?.instruction || '').toLowerCase();
    if (instruction.includes('stairs') || currentStep?.floor_change_type?.includes('stairs')) {
      return <StairsIcon sx={{ fontSize: 60 }} />;
    }
    if (instruction.includes('elevator') || currentStep?.floor_change_type?.includes('elevator')) {
      return <ElevatorIcon sx={{ fontSize: 60 }} />;
    }
    return <ArrowUpIcon sx={{ fontSize: 60 }} />;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#1a1a2e' }}>
      {/* Photo area */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {photoUrl && !imageError ? (
          <>
            {imageLoading && (
              <Box sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#1a1a2e',
              }}>
                <CircularProgress />
              </Box>
            )}
            <Box
              component="img"
              src={photoUrl}
              alt={currentStep?.instruction || 'Navigation step'}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageError(true);
                setImageLoading(false);
              }}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain', // Show entire photo without cropping
                objectPosition: 'center',
                display: imageLoading ? 'none' : 'block',
                bgcolor: '#1a1a2e', // Background for letterboxing
              }}
            />
          </>
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(180deg, #2d2d44 0%, #1a1a2e 100%)',
              color: 'grey.500',
            }}
          >
            {getDirectionIcon()}
            <Typography variant="body1" sx={{ mt: 2 }}>
              {currentStep?.instruction || 'Follow the route'}
            </Typography>
          </Box>
        )}

        {/* Instruction overlay on photo */}
        {photoUrl && !imageError && !imageLoading && (
          <Paper
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 3,
              py: 2,
              bgcolor: 'rgba(0,0,0,0.85)',
              borderRadius: 2,
              maxWidth: '90%',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', textAlign: 'center' }}>
              {currentStep?.instruction || 'Continue forward'}
            </Typography>
            {currentStep?.landmark && (
              <Typography variant="body2" sx={{ color: 'info.light', textAlign: 'center', mt: 0.5 }}>
                Look for: {currentStep.landmark}
              </Typography>
            )}
            {currentStep?.distance_meters > 0 && (
              <Typography variant="body2" sx={{ color: 'grey.400', textAlign: 'center', mt: 0.5 }}>
                {Math.round(currentStep.distance_meters)} meters
              </Typography>
            )}
          </Paper>
        )}

        {/* Step counter badge */}
        <Chip
          label={`Step ${stepNumber} of ${totalSteps}`}
          size="small"
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            bgcolor: 'rgba(0,0,0,0.7)',
            color: 'white',
          }}
        />

        {/* Floor change indicator */}
        {currentStep?.floor_change_type && currentStep.floor_change_type !== 'none' && (
          <Chip
            icon={currentStep.floor_change_type.includes('stairs') ? <StairsIcon /> : <ElevatorIcon />}
            label={currentStep.floor_change_type.includes('up') ? 'Go Up' : 'Go Down'}
            color={currentStep.floor_change_type.includes('stairs') ? 'secondary' : 'primary'}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
            }}
          />
        )}
      </Box>

      {/* Navigation buttons */}
      <Box sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: 'background.paper',
      }}>
        <Button
          variant="outlined"
          startIcon={<PrevIcon />}
          onClick={onPrev}
          disabled={stepNumber === 1}
        >
          Back
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" fontWeight="medium">
            {currentStep?.instruction || 'Navigate'}
          </Typography>
          {currentStep?.distance_meters > 0 && (
            <Typography variant="caption" color="text.secondary">
              {Math.round(currentStep.distance_meters)}m
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          endIcon={<NextIcon />}
          onClick={onNext}
        >
          {isLastStep ? 'Finish' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
};

// Map auto-center and floor change component
const MapController = ({ center, currentFloorId }) => {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom(), { animate: true, duration: 0.5 });
    }
  }, [center, currentFloorId, map]);

  return null;
};

// Navigation Map - shows only route line and POIs
const NavigationMap = ({
  buildingData,
  route,
  currentFloorId,
  currentStepIndex,
  destination,
}) => {
  const currentFloor = buildingData?.floors?.find(f => f.id === currentFloorId);

  if (!currentFloor) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Loading floor...</Typography>
      </Box>
    );
  }

  const imageUrl = currentFloor.image_url?.startsWith('http')
    ? currentFloor.image_url
    : `${API_BASE_URL}${currentFloor.image_url}`;

  // Calculate bounds
  const getBounds = () => {
    if (currentFloor.bounds_north && currentFloor.bounds_south) {
      return [
        [parseFloat(currentFloor.bounds_south), parseFloat(currentFloor.bounds_west)],
        [parseFloat(currentFloor.bounds_north), parseFloat(currentFloor.bounds_east)],
      ];
    }
    return [
      [0, 0],
      [currentFloor.image_height || 100, currentFloor.image_width || 100],
    ];
  };

  const bounds = getBounds();

  // Convert pixel position to map coordinates
  const pixelToLatLng = (x, y) => {
    if (currentFloor.bounds_north) {
      const latRange = parseFloat(currentFloor.bounds_north) - parseFloat(currentFloor.bounds_south);
      const lngRange = parseFloat(currentFloor.bounds_east) - parseFloat(currentFloor.bounds_west);
      const lat = parseFloat(currentFloor.bounds_north) - (y / currentFloor.image_height) * latRange;
      const lng = parseFloat(currentFloor.bounds_west) + (x / currentFloor.image_width) * lngRange;
      return [lat, lng];
    }
    return [y, x];
  };

  // Build route line from steps on current floor
  const routeStepsOnFloor = route?.steps?.filter(s => s.floor_plan_id === currentFloorId) || [];
  const routePoints = [];

  routeStepsOnFloor.forEach(step => {
    if (step.path_geometry?.start) {
      const startPos = pixelToLatLng(step.path_geometry.start.x, step.path_geometry.start.y);
      if (routePoints.length === 0 ||
          routePoints[routePoints.length - 1][0] !== startPos[0] ||
          routePoints[routePoints.length - 1][1] !== startPos[1]) {
        routePoints.push(startPos);
      }
    }
    if (step.path_geometry?.end) {
      routePoints.push(pixelToLatLng(step.path_geometry.end.x, step.path_geometry.end.y));
    }
  });

  // Get current step position
  const currentStep = route?.steps?.[currentStepIndex];
  const currentPosition = currentStep?.path_geometry?.start
    ? pixelToLatLng(currentStep.path_geometry.start.x, currentStep.path_geometry.start.y)
    : routePoints[0];

  // Get destination position
  const lastStepOnFloor = routeStepsOnFloor[routeStepsOnFloor.length - 1];
  const destPosition = lastStepOnFloor?.path_geometry?.end
    ? pixelToLatLng(lastStepOnFloor.path_geometry.end.x, lastStepOnFloor.path_geometry.end.y)
    : null;

  // Get POIs from floor plan
  const pois = currentFloor.pointsOfInterest || currentFloor.points_of_interest || [];

  // Create marker icons
  const currentIcon = new L.DivIcon({
    className: 'current-position-marker',
    html: `<div style="
      width: 20px;
      height: 20px;
      background: #FF9800;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(255,152,0,0.5);
    "></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });

  const destinationIcon = new L.DivIcon({
    className: 'destination-marker',
    html: `<div style="
      width: 28px;
      height: 28px;
      background: #4CAF50;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 3px 10px rgba(76,175,80,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      </svg>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      <style>{`
        @keyframes dash {
          to { stroke-dashoffset: -20; }
        }
        .animated-route {
          stroke-dasharray: 10, 10;
          animation: dash 1s linear infinite;
        }
      `}</style>

      <MapContainer
        center={currentPosition || [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2]}
        zoom={20}
        style={{ width: '100%', height: '100%' }}
        zoomControl={false}
        attributionControl={false}
      >
        <MapController center={currentPosition} currentFloorId={currentFloorId} />
        <ImageOverlay url={imageUrl} bounds={bounds} opacity={0.95} />

        {/* Route line */}
        {routePoints.length > 1 && (
          <Polyline
            positions={routePoints}
            pathOptions={{
              color: '#2196F3',
              weight: 6,
              opacity: 0.9,
              dashArray: '10, 10',
              className: 'animated-route',
            }}
          />
        )}

        {/* Current position marker */}
        {currentPosition && (
          <Marker position={currentPosition} icon={currentIcon}>
            <Tooltip permanent direction="top" offset={[0, -15]}>
              You are here
            </Tooltip>
          </Marker>
        )}

        {/* Destination marker */}
        {destPosition && currentFloorId === lastStepOnFloor?.floor_plan_id && (
          <Marker position={destPosition} icon={destinationIcon}>
            <Tooltip permanent direction="top" offset={[0, -20]}>
              {destination?.node?.name || 'Destination'}
            </Tooltip>
          </Marker>
        )}

        {/* POI markers with labels */}
        {pois.map((poi) => {
          const poiPos = poi.geo_lat && poi.geo_lng
            ? [parseFloat(poi.geo_lat), parseFloat(poi.geo_lng)]
            : pixelToLatLng(poi.x_position || poi.x, poi.y_position || poi.y);

          const poiIcon = new L.DivIcon({
            className: 'poi-marker',
            html: `<div style="
              width: 24px;
              height: 24px;
              background: #9C27B0;
              border: 2px solid white;
              border-radius: 50%;
              box-shadow: 0 2px 6px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              color: white;
            ">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          });

          return (
            <Marker key={poi.id} position={poiPos} icon={poiIcon}>
              <Tooltip permanent direction="right" offset={[15, 0]} className="poi-label">
                {poi.name || poi.label || 'POI'}
              </Tooltip>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Floor indicator */}
      <Chip
        label={currentFloor.floor_name || `Floor ${currentFloor.floor_number}`}
        size="small"
        color="primary"
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 1000,
        }}
      />

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          zIndex: 1000,
          p: 1,
          bgcolor: 'rgba(255,255,255,0.95)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: '#FF9800', borderRadius: '50%' }} />
          <Typography variant="caption">You</Typography>
          <Box sx={{ width: 12, height: 12, bgcolor: '#4CAF50', borderRadius: '50%', ml: 1 }} />
          <Typography variant="caption">Destination</Typography>
        </Box>
      </Paper>
    </Box>
  );
};

// Main Navigation Simulator Component
const NavigationSimulator = ({
  buildingData,
  route,
  entrance,
  destination,
  onDestinationChange,
  isMobile = false,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentFloorId, setCurrentFloorId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMap, setShowMap] = useState(!isMobile);

  const totalSteps = route?.steps?.length || 0;
  const currentStep = route?.steps?.[currentStepIndex];
  // isComplete is true AFTER completing the last step (when index equals or exceeds total)
  const isComplete = currentStepIndex >= totalSteps && totalSteps > 0;

  // Initialize floor from first step
  useEffect(() => {
    if (route?.steps?.length > 0) {
      const firstStep = route.steps[0];
      setCurrentFloorId(firstStep.floor_plan_id);
    } else if (entrance?.floor_plan_id) {
      setCurrentFloorId(entrance.floor_plan_id);
    } else if (buildingData?.floors?.length > 0) {
      setCurrentFloorId(buildingData.floors[0].id);
    }
  }, [route, entrance, buildingData]);

  // Update floor when step changes
  useEffect(() => {
    if (currentStep?.floor_plan_id) {
      setCurrentFloorId(currentStep.floor_plan_id);
    }
  }, [currentStep]);

  // Auto-play navigation
  useEffect(() => {
    if (isPlaying && route?.steps) {
      const interval = setInterval(() => {
        setCurrentStepIndex(prev => {
          // Allow advancing to completion (prev = totalSteps means complete)
          if (prev < route.steps.length) {
            return prev + 1;
          }
          setIsPlaying(false);
          return prev;
        });
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, route]);

  const handleNext = () => {
    // Allow advancing up to and including reaching completion (index = totalSteps)
    if (currentStepIndex < totalSteps) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleRestart = () => {
    setCurrentStepIndex(0);
    if (route?.steps?.[0]?.floor_plan_id) {
      setCurrentFloorId(route.steps[0].floor_plan_id);
    }
  };

  // Progress: 100% when complete, otherwise based on current step
  const progress = totalSteps > 0 ? Math.min(((currentStepIndex + 1) / totalSteps) * 100, 100) : 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Progress bar */}
      {totalSteps > 0 && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 4 }}
        />
      )}

      {/* Main content area */}
      <Box sx={{ flex: 1, display: 'flex', position: 'relative', minHeight: 0 }}>
        {/* Photo/Instruction View */}
        <Box sx={{ flex: showMap && !isMobile ? 2 : 1, height: '100%', position: 'relative' }}>
          {isComplete ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#1a1a2e',
                p: 3,
              }}
            >
              <ArrivalScreen
                destination={route?.destination || destination?.node}
                destinationPhotoUrl={(() => {
                  const lastStep = route?.steps?.[totalSteps - 1];
                  let url = lastStep?.photo_url || lastStep?.photoUrl;
                  if (url && url.startsWith('/uploads/')) {
                    url = url.replace('/uploads/', '/api/uploads/');
                  }
                  return url ? (url.startsWith('http') ? url : `${API_BASE_URL}${url}`) : null;
                })()}
                openDoorUrl={(() => {
                  // Check route destination first (from API response)
                  const routeDest = route?.destination;
                  if (routeDest?.openDoorUrl) return routeDest.openDoorUrl;
                  if (routeDest?.metadata?.openDoorUrl) return routeDest.metadata.openDoorUrl;
                  // Check config array (location custom fields stored as array)
                  if (Array.isArray(routeDest?.config)) {
                    const field = routeDest.config.find(f => f.name === 'openDoorUrl');
                    if (field?.value) return field.value;
                  } else if (routeDest?.config?.openDoorUrl) {
                    return routeDest.config.openDoorUrl;
                  }
                  // Check destination prop (from selected location)
                  const destNode = destination?.node;
                  if (destNode?.openDoorUrl) return destNode.openDoorUrl;
                  if (destNode?.metadata?.openDoorUrl) return destNode.metadata.openDoorUrl;
                  return null;
                })()}
                onClose={handleRestart}
              />
            </Box>
          ) : (
            <StepPhotoView
              currentStep={currentStep}
              stepNumber={currentStepIndex + 1}
              totalSteps={totalSteps}
              onNext={handleNext}
              onPrev={handlePrev}
              isLastStep={currentStepIndex === totalSteps - 1}
            />
          )}
        </Box>

        {/* Map Panel */}
        {showMap && (
          <Box
            sx={{
              ...(isMobile ? {
                position: 'absolute',
                bottom: 80,
                right: 8,
                width: 180,
                height: 180,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 3,
              } : {
                flex: 1,
                borderLeft: 1,
                borderColor: 'divider',
                position: 'relative',
                minWidth: 350,
                maxWidth: 450,
              }),
            }}
          >
            <NavigationMap
              buildingData={buildingData}
              route={route}
              currentFloorId={currentFloorId}
              currentStepIndex={currentStepIndex}
              destination={destination}
            />
          </Box>
        )}

        {/* Map toggle button */}
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: showMap && !isMobile ? 358 : 8,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'background.paper' },
            zIndex: 10,
          }}
          onClick={() => setShowMap(!showMap)}
          size={isMobile ? 'medium' : 'small'}
        >
          {showMap ? <CloseIcon /> : <MapIcon />}
        </IconButton>
      </Box>

      {/* Bottom control bar */}
      <Paper sx={{ p: isMobile ? 1 : 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 1 : 2,
          justifyContent: 'center',
        }}>
          {/* Navigation controls */}
          <IconButton
            onClick={handlePrev}
            disabled={currentStepIndex === 0}
            size={isMobile ? 'medium' : 'small'}
          >
            <PrevIcon />
          </IconButton>

          <IconButton
            color="primary"
            onClick={() => setIsPlaying(!isPlaying)}
            disabled={totalSteps === 0 || isComplete}
            size={isMobile ? 'medium' : 'small'}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </IconButton>

          <IconButton
            onClick={handleNext}
            disabled={isComplete}
            size={isMobile ? 'medium' : 'small'}
          >
            <NextIcon />
          </IconButton>

          <Divider orientation="vertical" flexItem />

          <IconButton
            onClick={handleRestart}
            disabled={totalSteps === 0}
            size={isMobile ? 'medium' : 'small'}
          >
            <RestartIcon />
          </IconButton>

          {!isMobile && (
            <>
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" color="text.secondary">
                {isComplete ? (
                  'Arrived!'
                ) : (
                  <>
                    Step {currentStepIndex + 1} of {totalSteps}
                    {currentStep?.floor_change_type && currentStep.floor_change_type !== 'none' && (
                      <Chip
                        size="small"
                        icon={currentStep.floor_change_type.includes('stairs') ? <StairsIcon /> : <ElevatorIcon />}
                        label={currentStep.floor_change_type.includes('up') ? 'Up' : 'Down'}
                        color={currentStep.floor_change_type.includes('stairs') ? 'secondary' : 'primary'}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </>
                )}
              </Typography>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default NavigationSimulator;
